import { useLiveQuery } from "dexie-react-hooks";
import {
  BarChart3,
  BookOpenCheck,
  Building2,
  Cloud,
  CircleHelp,
  CreditCard,
  DatabaseBackup,
  FileSpreadsheet,
  GitBranch,
  History,
  Landmark,
  LogOut,
  Settings,
  ShieldCheck,
  Tags,
  User,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Card, PageHeader, StatusBadge } from "../components/ui";
import { useAuth } from "../lib/auth";
import { db } from "../lib/db";
import { flushSyncQueue } from "../lib/sync";
import { refreshFromServer } from "../lib/data";

const roleLabels: Record<string, string> = {
  OWNER: "Chủ cửa hàng",
  MANAGER: "Quản lý",
  STAFF: "Nhân viên",
  ACCOUNTANT: "Kế toán",
};

const groups = [
  {
    title: "KINH DOANH",
    links: [
      { to: "/products", label: "Danh mục sản phẩm / SKU", icon: Tags, hint: "Product Master độc lập với tồn kho thực tế" },
      { to: "/partners", label: "Khách hàng & nhà cung cấp", icon: Users, hint: "Danh bạ đối tác khách hàng & nhà cung cấp" },
      { to: "/debts", label: "Công nợ", icon: Users, hint: "Khách nợ và nợ nhà cung cấp" },
      { to: "/reports", label: "Báo cáo", icon: BarChart3, hint: "6 báo cáo trọng yếu", roles: ["OWNER", "MANAGER", "ACCOUNTANT"] },
      { to: "/tax", label: "Thuế & nghĩa vụ", icon: Landmark, hint: "Tình trạng dữ liệu & pháp lý", roles: ["OWNER", "MANAGER", "ACCOUNTANT"] },
    ],
  },
  {
    title: "CỬA HÀNG",
    links: [
      { to: "/settings", label: "Thông tin cửa hàng", icon: Settings, hint: "Thông tin cửa hàng & thiết lập" },
      { to: "/users", label: "Người dùng & phân quyền", icon: ShieldCheck, hint: "Chủ cửa hàng · Quản lý · Nhân viên · Kế toán", roles: ["OWNER", "MANAGER"] },
      { to: "/branches", label: "Chi nhánh", icon: GitBranch, hint: "Chuyển đổi điểm bán & quản lý chi nhánh", roles: ["OWNER", "MANAGER"] },
      { to: "/closing", label: "Đóng sổ ngày", icon: BookOpenCheck, hint: "Đối chiếu két tiền & chốt sổ", roles: ["OWNER", "MANAGER"] },
      { to: "/activity", label: "Lịch sử hoạt động", icon: History, hint: "Truy vết các thay đổi quan trọng", roles: ["OWNER", "MANAGER", "ACCOUNTANT"] },
    ],
  },
  {
    title: "DỊCH VỤ BLed",
    links: [
      { to: "/subscription", label: "Gói đang sử dụng", icon: CreditCard, hint: "Gói dịch vụ, thời hạn và trạng thái" },
      { to: "/subscription", label: "Thanh toán & hóa đơn dịch vụ", icon: Building2, hint: "Gia hạn gói dịch vụ và thanh toán", roles: ["OWNER"] },
      { to: "/subscription", label: "Giới hạn sử dụng", icon: ShieldCheck, hint: "Hạn mức chi nhánh, người dùng và tính năng", roles: ["OWNER"] },
    ],
  },
  {
    title: "DỮ LIỆU",
    links: [
      { to: "/help", label: "Hướng dẫn sử dụng & quy trình dữ liệu", icon: CircleHelp, hint: "Quy trình chuẩn từ SKU → nhập hàng → bán hàng → tiền/công nợ → đóng sổ" },
      { to: "/inventory/import", label: "Nhập liệu thông minh", icon: FileSpreadsheet, hint: "CSV/XLSX → kiểm tra → phiếu nhập kho", roles: ["OWNER", "MANAGER", "STAFF"] },
      { to: "/data-health", label: "Kiểm tra dữ liệu", icon: ShieldCheck, hint: "Đối chiếu kho, giá vốn, bán/nhập, tiền và công nợ theo ledger", roles: ["OWNER", "MANAGER", "ACCOUNTANT"] },
      { to: "/data-tools", label: "Sao lưu & xuất dữ liệu", icon: DatabaseBackup, hint: "Snapshot, xuất CSV và nhập danh mục", roles: ["OWNER", "MANAGER", "ACCOUNTANT"] },
    ],
  },
  {
    title: "TÀI KHOẢN",
    links: [
      { to: "/settings", label: "Hồ sơ cá nhân & bảo mật", icon: User, hint: "Thông tin tài khoản đăng nhập" },
    ],
  },
] as const;

export function MorePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const pending = useLiveQuery(() => db.syncQueue.where("status").anyOf("PENDING", "FAILED", "SYNCING").count(), [], 0);

  async function syncNow() {
    const result = await flushSyncQueue();
    if (result.unauthorized) {
      auth.invalidateSession();
      return;
    }
    if (navigator.onLine && !auth.demo) await refreshFromServer();
  }

  const rawRole = auth.session?.user.role ?? "STAFF";
  const localizedRole = roleLabels[rawRole] ?? rawRole;

  return (
    <div className="page-stack">
      <PageHeader title="Thêm" description="Các chức năng quản trị, thiết lập và nghiệp vụ vận hành" />

      <Card className="profile-card">
        <img src="/logo.png" alt="" />
        <div>
          <strong>{auth.session?.store.name ?? "BLedger"}</strong>
          <small>{auth.session?.user.name ?? "Chủ cửa hàng"} · {localizedRole}</small>
        </div>
        {auth.demo ? <StatusBadge tone="info">Demo</StatusBadge> : null}
      </Card>

      {!auth.demo ? (
        <button className="sync-panel" onClick={() => void syncNow()} disabled={!navigator.onLine}>
          <Cloud size={22} />
          <div>
            <strong>Đồng bộ dữ liệu</strong>
            <small>{pending ? `${pending} giao dịch đang chờ hoặc cần thử lại` : "Dữ liệu cục bộ đã xử lý hết hàng đợi"}</small>
          </div>
          <StatusBadge tone={pending ? "warning" : "success"}>{pending ? "Kiểm tra" : "Sẵn sàng"}</StatusBadge>
        </button>
      ) : null}

      {groups.map((group) => (
        <section key={group.title}>
          <div className="section-title"><div><h2>{group.title}</h2></div></div>
          <Card className="menu-list">
            {group.links
              .filter((link) => {
                const roles = "roles" in link ? link.roles : undefined;
                return !roles || roles.includes((auth.session?.user.role ?? "STAFF") as never);
              })
              .map(({ to, label, icon: Icon, hint }, index) => (
                <Link key={`${label}-${index}`} to={to}>
                  <span className="menu-icon"><Icon size={20} /></span>
                  <div><strong>{label}</strong><small>{hint}</small></div>
                  <span>›</span>
                </Link>
              ))}
          </Card>
        </section>
      ))}

      <button
        className="btn btn-destructive btn-lg"
        onClick={async () => {
          await auth.logout();
          navigate("/login", { replace: true });
        }}
      >
        <LogOut size={20} /> Đăng xuất
      </button>
    </div>
  );
}
